import {
  Box,
  Chip,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { CheckCircleOutline } from "@mui/icons-material"
import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Link as RouterLink } from "react-router-dom"
import { Locker } from "shared/locker"
import { getLockerDisplayValues } from "src/hooks/useLockerDisplayValues"
import { Sstv2MigrationRow } from "./sstv2MigrationTypes"

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

/**
 * The migration entities (grid) and the fleet V1/V2 counts (rollup) are derived
 * from independent sources — the migration event stream vs `locker.sstApiVersion`
 * — and never cross-checked. A divergence is the "did we miss/drop events?"
 * signal (exactly the stale-consumer situation). This panel surfaces both
 * directions of divergence (Item 8, DEV-193).
 */
export type ReconciliationResult = {
  /** V2 in the fleet but NO OPERATIONAL/COMPLETED migration record. */
  v2WithoutRecord: Locker[]
  /** OPERATIONAL/COMPLETED migration record but the fleet locker isn't V2. */
  recordWithoutV2: Sstv2MigrationRow[]
}

const CUTOVER_PHASES = new Set(["OPERATIONAL", "COMPLETED"])

/**
 * Pure reconciliation. A migration record maps to a fleet locker by V2 SST id
 * (`row.migrationId === locker.sstLockerId`) or by the created SL2 Thing id
 * (`row.createdSl2LockerThingId === locker.id`).
 */
export const computeReconciliation = (
  lockers: Locker[],
  rows: Sstv2MigrationRow[],
): ReconciliationResult => {
  const cutoverRows = rows.filter((r) => CUTOVER_PHASES.has(r.phase))
  const recordSstIds = new Set(
    cutoverRows.map((r) => r.migrationId).filter(Boolean) as string[],
  )
  const recordThingIds = new Set(
    cutoverRows
      .map((r) => r.createdSl2LockerThingId)
      .filter(Boolean) as string[],
  )

  const v2Lockers = lockers.filter((l) => l.sstApiVersion === "v2")
  const v2SstIds = new Set(
    v2Lockers.map((l) => l.sstLockerId).filter(Boolean) as string[],
  )
  const v2ThingIds = new Set(v2Lockers.map((l) => l.id))

  const v2WithoutRecord = v2Lockers.filter(
    (l) =>
      !(l.sstLockerId && recordSstIds.has(l.sstLockerId)) &&
      !recordThingIds.has(l.id),
  )

  const recordWithoutV2 = cutoverRows.filter(
    (r) =>
      !(r.migrationId && v2SstIds.has(r.migrationId)) &&
      !(r.createdSl2LockerThingId && v2ThingIds.has(r.createdSl2LockerThingId)),
  )

  return { v2WithoutRecord, recordWithoutV2 }
}

const DiscrepancyTable: FC<{
  titleKey: string
  hintKey: string
  count: number
  children: React.ReactNode
}> = ({ titleKey, hintKey, count, children }) => {
  const { t } = useTranslation()
  return (
    <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, flex: 1, minWidth: 300 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          {t(titleKey)}
        </Typography>
        <Chip
          size="small"
          label={count}
          color={count > 0 ? "info" : "default"}
          variant="outlined"
        />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {t(hintKey)}
      </Typography>
      <Box sx={{ mt: 1.5 }}>{children}</Box>
    </Box>
  )
}

export const Sstv2ReconciliationPanel: FC<{
  lockers: Locker[]
  rows: Sstv2MigrationRow[]
  loading: boolean
}> = ({ lockers, rows, loading }) => {
  const { t } = useTranslation()
  const { v2WithoutRecord, recordWithoutV2 } = useMemo(
    () => computeReconciliation(lockers, rows),
    [lockers, rows],
  )

  const clean =
    !loading && v2WithoutRecord.length === 0 && recordWithoutV2.length === 0

  if (clean) {
    return (
      <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <CheckCircleOutline sx={{ color: "success.main" }} fontSize="small" />
          <Typography variant="body2" color="text.secondary">
            {t("migrationReconcileClean")}
          </Typography>
        </Stack>
      </Box>
    )
  }

  return (
    <Stack data-ui="UI-SL2W-260"
      direction="row"
      spacing={2.5}
      flexWrap="wrap"
      rowGap={2.5}
      sx={{ mb: 2.5 }}
    >
      <DiscrepancyTable
        titleKey="migrationReconcileV2NoRecord"
        hintKey="migrationReconcileV2NoRecordHint"
        count={v2WithoutRecord.length}
      >
        {v2WithoutRecord.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("lockerName")}</TableCell>
                <TableCell>{t("sstLockerId")}</TableCell>
                <TableCell>{t("actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {v2WithoutRecord.slice(0, 50).map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{getLockerDisplayValues(l).i18n.name}</TableCell>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace">
                      {l.sstLockerId || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Link component={RouterLink} to={`/sa/lockers/${l.id}`}>
                      {t("viewLocker")}
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DiscrepancyTable>

      <DiscrepancyTable
        titleKey="migrationReconcileRecordNoV2"
        hintKey="migrationReconcileRecordNoV2Hint"
        count={recordWithoutV2.length}
      >
        {recordWithoutV2.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("tenantName")}</TableCell>
                <TableCell>{t("migrationV1Locker")}</TableCell>
                <TableCell>{t("migrationPhase")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recordWithoutV2.slice(0, 50).map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.tenantName}</TableCell>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace">
                      {r.v1LockerName}
                    </Typography>
                  </TableCell>
                  <TableCell>{r.phase}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DiscrepancyTable>
    </Stack>
  )
}

export default Sstv2ReconciliationPanel
